import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../contexts/ContentContext';
import { getMediaUrl } from '../utils/mediaUrl';
import { programWhatsAppUrl } from '../utils/whatsapp';
import { trackContact, trackLeadIntent } from '../utils/metaPixel';
import { sanitizeCmsText, sanitizeCmsHtml } from '../utils/cmsText';
import { resolveFeaturedProgramLink, getFeaturedProgramImage } from '../utils/formationCatalog';
import { filterUpcomingSessions } from '../utils/sessionDates';
import { resolvePhotoAlbums, resolveVideoAlbums, countGalleryVideos } from '../utils/galleryAlbums';
import { resolveTestimonialVideo } from '../utils/testimonialVideo';
import GalleryVideoItem from './GalleryVideoItem';
import TestimonialVideoBlock from './TestimonialVideoBlock';

const Home = () => {
  const { getContent } = useContent();
  const expertGallery = getContent('home.expertGallery') || {};
  const photoAlbums = resolvePhotoAlbums(expertGallery);
  const videoAlbums = resolveVideoAlbums(expertGallery);
  const [activeGalleryTab, setActiveGalleryTab] = useState('photos');
  
  const heroContent = getContent('home.hero') || {};
  const featuredPrograms = getContent('home.featuredPrograms') || {};
  const featuresHighlight = getContent('home.featuresHighlight') || {};
  const expertTestimonials = getContent('home.expertTestimonials') || {};
  const participantTestimonials = getContent('home.participantTestimonials') || {};
  const upcomingSessions = getContent('home.upcomingSessions') || {};
  const homeCta = getContent('home.cta') || {};
  const galleryTabs = expertGallery.tabs || [
    { id: 'photos', icon: 'fas fa-images', text: 'Photos' },
    { id: 'videos', icon: 'fas fa-video', text: 'Vidéos' },
  ];

  // Update active tab when expertGallery.defaultTab changes
  useEffect(() => {
    if (expertGallery.defaultTab) {
      setActiveGalleryTab(expertGallery.defaultTab);
    }
  }, [expertGallery.defaultTab]);

  useEffect(() => {
    const video = document.querySelector('.hero-video');
    
    if (video) {
      const videoUrl = getMediaUrl(heroContent.videoUrl) || '/video.mp4';
      
      const showVideo = () => {
        video.classList.add('loaded');
      };

      // Update video source and force reload
      const source = video.querySelector('source');
      if (source && source.getAttribute('src') !== videoUrl) {
        source.setAttribute('src', videoUrl);
        video.load();
      }
      
      const playVideo = async () => {
        try {
          video.muted = true;
          video.playsInline = true;
          await video.play();
        } catch {
          // Autoplay blocked on mobile — still show first frame via .loaded
        }
      };

      const handleReady = () => {
        showVideo();
        playVideo();
      };

      video.addEventListener('loadeddata', handleReady, { once: true });
      video.addEventListener('canplay', showVideo, { once: true });
      
      if (video.readyState >= 2) {
        handleReady();
      }

      return () => {
        video.removeEventListener('loadeddata', handleReady);
        video.removeEventListener('canplay', showVideo);
      };
    }
  }, [heroContent.videoUrl]);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero" id="hero">
        <video 
          key={getMediaUrl(heroContent.videoUrl) || '/video.mp4'}
          className="hero-video" 
          autoPlay 
          muted 
          playsInline 
          preload="auto"
          loop
          poster={getMediaUrl(heroContent.posterUrl) || ''}
          webkit-playsinline="true"
          x-webkit-airplay="deny"
        >
          <source src={getMediaUrl(heroContent.videoUrl) || '/video.mp4'} type="video/mp4" />
        </video>
        <div className="hero-particles">
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
        </div>
        <div className="hero-container">
          <div className="hero-content">
            <h1
              className="hero-title"
              dangerouslySetInnerHTML={{
                __html: sanitizeCmsHtml(
                  heroContent.title,
                  'Dévoué à faire évoluer votre carrière'
                ),
              }}
            />
            <p
              className="hero-subtitle"
              dangerouslySetInnerHTML={{
                __html: sanitizeCmsHtml(
                  heroContent.subtitle,
                  'Leader de la formation dentaire en Algérie. Spécialisé en technologie Cone Beam CBCT, chirurgie orale avancée, prothèse, implantologie et orthodontie moderne.'
                ),
              }}
            />
            <div className="hero-stats">
              {(heroContent.stats || []).map((stat, index) => (
                <div key={index} className="stat">
                  <span className="stat-number">{stat.number}</span>
                  <span className="stat-label">{stat.label}</span>
                </div>
              ))}
            </div>
            <div className="hero-buttons">
              {(heroContent.buttons || []).map((button, index) => (
                button.link?.startsWith('http') ? (
                  <a
                    key={index}
                    href={button.link}
                    className={`btn btn-${button.type}${index === 0 ? ' btn-hero-primary' : ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className={button.icon}></i>
                    {button.text}
                  </a>
                ) : (
                  <Link
                    key={index}
                    to={button.link}
                    className={`btn btn-${button.type}${index === 0 ? ' btn-hero-primary' : ''}`}
                  >
                    <i className={button.icon}></i>
                    {button.text}
                  </Link>
                )
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Programs */}
      <section className="featured-programs" id="featured-programs">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{sanitizeCmsText(featuredPrograms.title, 'Prochaines formations')}</h2>
            <p className="section-subtitle">
              {sanitizeCmsText(featuredPrograms.subtitle, 'Des programmes de formation avancés conçus pour élever votre pratique dentaire')}
            </p>
          </div>
          <div className="programs-grid">
            {(featuredPrograms.programs || []).map((program, index) => {
              const programImage = getFeaturedProgramImage(program);
              const aspectRatios = {
                '1:1': 1,
                '4:3': 4 / 3,
                '16:9': 16 / 9,
                '4:5': 4 / 5,
                '3:2': 3 / 2,
                '21:9': 21 / 9,
              };
              const hasImage = Boolean(programImage);
              const aspectKey = program.imageSettings?.aspectRatio || '4:5';
              const aspectValue = aspectRatios[aspectKey] || 4 / 5;

              return (
              <div key={program.id || index} className={`program-card ${index === 0 ? 'featured' : ''}`}>
                {program.badge && <span className="program-badge">{program.badge}</span>}
                <div
                  className={`program-image${hasImage ? '' : ' program-image--compact'}`}
                  style={
                    hasImage
                      ? {
                          position: 'relative',
                          width: '100%',
                          paddingBottom: `${(1 / aspectValue) * 100}%`,
                          overflow: 'hidden',
                        }
                      : undefined
                  }
                >
                  {hasImage ? (
                    <img
                      src={programImage}
                      alt={program.title}
                      className="program-image-element"
                      style={program.imageSettings ? {
                        transform: `translate(calc(-50% + ${program.imageSettings.position.x}px), calc(-50% + ${program.imageSettings.position.y}px)) scale(${program.imageSettings.scale})`,
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        minWidth: '100%',
                        minHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'cover',
                      } : {
                        objectFit: program.imageObjectFit || 'contain',
                      }}
                    />
                  ) : (
                    <div className="program-placeholder">
                      <i className={program.icon || 'fas fa-graduation-cap'}></i>
                    </div>
                  )}
                </div>
                <div className="program-content">
                  <h3>{program.title}</h3>
                  <p>{program.description}</p>
                  <div className="program-features">
                    {(program.features || []).map((feature, featureIndex) => (
                      <span key={featureIndex} className="feature">{feature}</span>
                    ))}
                  </div>
                  <div className="program-actions">
                    <Link to={resolveFeaturedProgramLink(program, getContent)} className="btn btn-outline">
                      <i className="fas fa-arrow-right"></i>
                      En savoir plus
                    </Link>
                    <a
                      href={programWhatsAppUrl(program, getContent)}
                      className="btn btn-success"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() =>
                        trackContact({
                          formationId: program.id || `featured_${index}`,
                          formationName: program.title,
                          source: 'home_featured_program_whatsapp',
                        })
                      }
                    >
                      <i className="fab fa-whatsapp"></i>
                      Recevoir le programme
                    </a>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        </div>
      </section>

      {/* Témoignages de nos Participants */}
      <section className="participant-testimonials" id="participant-testimonials">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{participantTestimonials.title || 'Témoignages de nos Participants'}</h2>
            <p className="section-subtitle">
              {participantTestimonials.subtitle || 'Découvrez comment nos formations ont transformé la carrière de dentistes à travers l\'Algérie'}
            </p>
          </div>
        </div>
        <div className="testimonials-carousel-wrapper">
          <div className="testimonials-carousel testimonials-carousel-participants">
            {/* First set of testimonials */}
            {(participantTestimonials.testimonials || []).map((participant, index) => (
              <div key={`participant-${participant.id}`} className="testimonial-card participant-testimonial">
                <div className="testimonial-header">
                  <div className="participant-photo">
                    {participant.photo ? (
                      <img src={participant.photo} alt={participant.name} />
                    ) : (
                      <i className="fas fa-user-doctor"></i>
                    )}
                  </div>
                  <div className="participant-info">
                    <h4>{participant.name}</h4>
                    <span className="participant-title">{participant.title}</span>
                    <span className="participant-formation">À propos: {participant.formation}</span>
                    <span className="participant-location">
                      <i className="fas fa-map-marker-alt"></i>
                      {participant.location} • {participant.date}
                    </span>
                  </div>
                </div>
                <div className="testimonial-content">
                  {participant.type === 'video' && resolveTestimonialVideo(participant) ? (
                    <TestimonialVideoBlock item={participant} name={participant.name} />
                  ) : (
                    <div className="testimonial-text">
                      <p>"{participant.text}"</p>
                    </div>
                  )}
                  <div className="testimonial-rating">
                    {[...Array(participant.rating || 5)].map((_, starIndex) => (
                      <i key={starIndex} className="fas fa-star"></i>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {/* Duplicate for seamless loop */}
            {(participantTestimonials.testimonials || []).map((participant, index) => (
              <div key={`participant-dup-${participant.id}`} className="testimonial-card participant-testimonial">
                <div className="testimonial-header">
                  <div className="participant-photo">
                    {participant.photo ? (
                      <img src={participant.photo} alt={participant.name} />
                    ) : (
                      <i className="fas fa-user-doctor"></i>
                    )}
                  </div>
                  <div className="participant-info">
                    <h4>{participant.name}</h4>
                    <span className="participant-title">{participant.title}</span>
                    <span className="participant-formation">À propos: {participant.formation}</span>
                    <span className="participant-location">
                      <i className="fas fa-map-marker-alt"></i>
                      {participant.location} • {participant.date}
                    </span>
                  </div>
                </div>
                <div className="testimonial-content">
                  {participant.type === 'video' && resolveTestimonialVideo(participant) ? (
                    <TestimonialVideoBlock item={participant} name={participant.name} />
                  ) : (
                    <div className="testimonial-text">
                      <p>"{participant.text}"</p>
                    </div>
                  )}
                  <div className="testimonial-rating">
                    {[...Array(participant.rating || 5)].map((_, starIndex) => (
                      <i key={starIndex} className="fas fa-star"></i>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Témoignages de nos Experts */}
      <section className="expert-testimonials" id="expert-testimonials">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{expertTestimonials.title || 'Témoignages de nos Experts'}</h2>
            <p className="section-subtitle">
              {expertTestimonials.subtitle || 'Découvrez l\'expertise et l\'expérience de nos formateurs de renommée internationale'}
            </p>
          </div>
        </div>
        <div className="testimonials-carousel-wrapper">
          <div className="testimonials-carousel testimonials-carousel-experts">
            {/* First set of testimonials */}
            {(expertTestimonials.testimonials || []).map((expert, index) => (
              <div key={`expert-${expert.id}`} className="testimonial-card expert-testimonial">
                <div className="testimonial-header">
                  <div className="expert-photo">
                    {expert.photo ? (
                      <img src={expert.photo} alt={expert.name} />
                    ) : (
                      <i className="fas fa-user-md"></i>
                    )}
                  </div>
                  <div className="expert-info">
                    <h4>{expert.name}</h4>
                    <span className="expert-title">{expert.title}</span>
                    <span className="expert-formation">Formation: {expert.formation}</span>
                  </div>
                  {expert.type === 'video' && resolveTestimonialVideo(expert) && (
                    <div className="video-indicator">
                      <i className="fab fa-youtube"></i>
                      <span>Témoignage Vidéo</span>
                    </div>
                  )}
                </div>
                <div className="testimonial-content">
                  {expert.type === 'video' && resolveTestimonialVideo(expert) ? (
                    <TestimonialVideoBlock item={expert} name={expert.name} />
                  ) : (
                    <div className="testimonial-text">
                      <p>"{expert.text}"</p>
                    </div>
                  )}
                  <div className="expert-specialties">
                    {(expert.specialties || []).map((specialty, specialtyIndex) => (
                      <span key={specialtyIndex} className="specialty-badge">{specialty}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {/* Duplicate for seamless loop */}
            {(expertTestimonials.testimonials || []).map((expert, index) => (
              <div key={`expert-dup-${expert.id}`} className="testimonial-card expert-testimonial">
                <div className="testimonial-header">
                  <div className="expert-photo">
                    {expert.photo ? (
                      <img src={expert.photo} alt={expert.name} />
                    ) : (
                      <i className="fas fa-user-md"></i>
                    )}
                  </div>
                  <div className="expert-info">
                    <h4>{expert.name}</h4>
                    <span className="expert-title">{expert.title}</span>
                    <span className="expert-formation">Formation: {expert.formation}</span>
                  </div>
                </div>
                <div className="testimonial-content">
                  {expert.type === 'video' && resolveTestimonialVideo(expert) ? (
                    <TestimonialVideoBlock item={expert} name={expert.name} />
                  ) : (
                    <div className="testimonial-text">
                      <p>"{expert.text}"</p>
                    </div>
                  )}
                  <div className="expert-specialties">
                    {(expert.specialties || []).map((specialty, specialtyIndex) => (
                      <span key={specialtyIndex} className="specialty-badge">{specialty}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Highlight */}
      <section className="features-highlight" id="features">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{featuresHighlight.title || 'Pourquoi Choisir DentoXcellence ?'}</h2>
            <p className="section-subtitle">
              {featuresHighlight.subtitle || 'L\'excellence reconnue en formation dentaire avec des résultats exceptionnels'}
            </p>
          </div>
          <div className="features-showcase">
            {(featuresHighlight.features || []).map((feature, index) => (
              <div key={index} className={`feature-card ${index === 0 ? 'premium' : index === 1 ? 'excellence' : index === 2 ? 'expertise' : 'satisfaction'}`}>
                <div className="feature-photo">
                  {feature.photo ? (
                    <img src={feature.photo} alt={feature.title} />
                  ) : (
                    <div className="feature-placeholder">
                      <i className={feature.icon || 'fas fa-star'}></i>
                    </div>
                  )}
                </div>
                <div className="feature-content">
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                  <div className="feature-stats">
                    <span className="stat-highlight">{feature.stat}</span>
                    <span className="stat-desc">{feature.statDesc}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Training Sessions */}
      <section className="upcoming-sessions" id="upcoming-sessions">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{upcomingSessions.title || 'Calendrier 2026-2027'}</h2>
            <p className="section-subtitle">
              {upcomingSessions.subtitle || 'Inscrivez-vous dès maintenant aux formations à venir dans votre région'}
            </p>
          </div>
          <div className="sessions-grid">
            {filterUpcomingSessions(upcomingSessions.sessions || []).map((session, index) => {
              const sessionLink = session.link
                ? resolveFeaturedProgramLink({ link: session.link, title: session.title }, getContent)
                : '/formations#registration-form';
              const trainerName = session.trainer || session.instructor;
              const isExternalLink = sessionLink.startsWith('http');
              const buttonLabel = session.link ? 'Voir la formation' : "S'inscrire";
              const buttonIcon = session.link ? 'fas fa-arrow-right' : 'fas fa-user-plus';

              const handleSessionActionClick = () => {
                if (buttonLabel === "S'inscrire") {
                  trackLeadIntent({
                    formationName: session.title,
                    source: 'home_upcoming_session_inscribe',
                  });
                }
              };

              return (
              <div key={index} className="session-card">
                <div className={`session-badge ${(session.badge || '').toLowerCase()}`}>
                  {session.badgeIcon && <i className={session.badgeIcon}></i>}
                  {session.badge}
              </div>
              <div className="session-header">
                  <h3>{session.title}</h3>
                  {trainerName && (
                    <span className="session-trainer">avec {trainerName}</span>
                  )}
              </div>
              <div className="session-details">
                <div className="session-date">
                  <i className="fas fa-calendar"></i>
                    <span>{session.date}</span>
                </div>
                <div className="session-location">
                  <i className="fas fa-map-marker-alt"></i>
                    <span>{session.location}</span>
                </div>
                <div className="session-duration">
                  <i className="fas fa-clock"></i>
                    <span>{session.duration}</span>
                </div>
              </div>
              <div className="session-action">
                {isExternalLink ? (
                  <a href={sessionLink} className="btn btn-primary" target="_blank" rel="noopener noreferrer">
                    <i className={buttonIcon}></i>
                    {buttonLabel}
                  </a>
                ) : (
                  <Link to={sessionLink} className="btn btn-primary" onClick={handleSessionActionClick}>
                    <i className={buttonIcon}></i>
                    {buttonLabel}
                  </Link>
                )}
              </div>
            </div>
            );
            })}
          </div>
          <div className="sessions-cta">
            <p>{upcomingSessions.ctaText || 'Plus de sessions disponibles dans d\'autres villes'}</p>
            <Link to={upcomingSessions.ctaLink || '/formations'} className="btn btn-outline">
              <i className="fas fa-calendar-alt"></i>
              {upcomingSessions.ctaButton || 'Voir Toutes les Sessions'}
            </Link>
          </div>
              </div>
      </section>

      {/* Expert Gallery */}
      <section className="expert-gallery" id="expert-gallery">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{expertGallery.title || 'Notre institut en images'}</h2>
            <p className="section-subtitle">
              {expertGallery.subtitle || 'Instants de formation et conférences avec nos experts'}
            </p>
              </div>
          
          <div className="gallery-tabs" style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '2rem' }}>
            {galleryTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`gallery-tab ${activeGalleryTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveGalleryTab(tab.id)}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '25px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: activeGalleryTab === tab.id ? '#3b82f6' : '#f3f4f6',
                  color: activeGalleryTab === tab.id ? 'white' : '#6b7280',
                  fontWeight: activeGalleryTab === tab.id ? '600' : '400',
                  transition: 'all 0.3s ease',
                }}
              >
                <i className={tab.icon}></i>
                {tab.text}
              </button>
            ))}
          </div>

          <div className="gallery-wrapper">
            {/* Photos Tab Content */}
            {activeGalleryTab === 'photos' && (
              <div className="gallery-albums-home">
                {photoAlbums.map((album) => (
                  <div key={album.id || album.title} className="gallery-album-section">
                    {photoAlbums.length > 1 && (
                      <h3 className="gallery-album-title">{album.title}</h3>
                    )}
                    <div className="gallery-content photos-content">
                      {(album.photos || []).slice(0, photoAlbums.length > 1 ? 3 : 6).map((photo, index) => {
                        const photoUrl = getMediaUrl(photo);
                        return (
                          <div
                            key={`${album.id}-${index}`}
                            className="gallery-item"
                            style={photo.imageSettings?.aspectRatio ? { aspectRatio: 'auto' } : {}}
                          >
                            {photoUrl ? (
                              <div
                                style={{
                                  position: 'relative',
                                  width: '100%',
                                  paddingBottom: photo.imageSettings?.aspectRatio
                                    ? `${(1 / (() => {
                                        const ratios = {
                                          '1:1': 1,
                                          '4:3': 4 / 3,
                                          '16:9': 16 / 9,
                                          '4:5': 4 / 5,
                                          '3:2': 3 / 2,
                                          '21:9': 21 / 9,
                                        };
                                        return ratios[photo.imageSettings.aspectRatio] || 16 / 9;
                                      })()) * 100}%`
                                    : '56.25%',
                                  overflow: 'hidden',
                                }}
                              >
                                <img
                                  src={photoUrl}
                                  alt={photo.title}
                                  style={
                                    photo.imageSettings
                                      ? {
                                          transform: `translate(calc(-50% + ${photo.imageSettings.position.x}px), calc(-50% + ${photo.imageSettings.position.y}px)) scale(${photo.imageSettings.scale})`,
                                          position: 'absolute',
                                          top: '50%',
                                          left: '50%',
                                          minWidth: '100%',
                                          minHeight: '100%',
                                          width: 'auto',
                                          height: 'auto',
                                          objectFit: photo.imageSettings.objectFit || 'cover',
                                        }
                                      : {
                                          position: 'absolute',
                                          top: 0,
                                          left: 0,
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                        }
                                  }
                                />
                              </div>
                            ) : (
                              <div className="gallery-placeholder" title={photo.title}>
                                <i className={photo.icon || 'fas fa-camera'}></i>
                              </div>
                            )}
                            <div className="gallery-overlay">
                              <h4>{photo.title}</h4>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Videos Tab Content */}
            {activeGalleryTab === 'videos' && (
              <div className="gallery-albums-home">
                {countGalleryVideos(expertGallery) === 0 ? (
                  <div className="gallery-empty-state" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '3rem',
                    textAlign: 'center',
                    color: '#6b7280'
                  }}>
                    <i className="fab fa-youtube" style={{ fontSize: '4rem', color: '#ff0000', marginBottom: '1rem' }}></i>
                    <h3 style={{ margin: '0 0 1rem 0', color: '#374151' }}>Aucune vidéo pour le moment</h3>
                    <p style={{ margin: '0 0 1.5rem 0', maxWidth: '400px' }}>
                      Les vidéos YouTube ajoutées via l'admin apparaîtront ici.
                    </p>
                  </div>
                ) : (
                  videoAlbums.map((album) => (
                    <div key={album.id || album.title} className="gallery-album-section">
                      {videoAlbums.length > 1 && album.title && (
                        <h3 className="gallery-album-title">{album.title}</h3>
                      )}
                      <div className="gallery-content videos-content">
                        {(album.videos || [])
                          .slice(0, videoAlbums.length > 1 ? 3 : 6)
                          .map((video, index) => (
                            <GalleryVideoItem
                              key={`${album.id}-${index}`}
                              video={video}
                              index={index}
                              keyPrefix={`${album.id}-`}
                            />
                          ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="gallery-actions">
            {(expertGallery.actions || []).map((action, index) => {
              const isExternal = action.link?.startsWith('http');
              
              return isExternal ? (
                <a
                  key={index}
                  href={action.link}
                  className={`btn btn-${action.type}`}
                  target={action.target || '_blank'}
                  rel="noopener noreferrer"
                >
                  <i className={action.icon}></i>
                  {action.text}
                </a>
              ) : (
                <Link
                  key={index}
                  to={action.link || '/galerie'}
                  className={`btn btn-${action.type}`}
                >
                  <i className={action.icon}></i>
                  {action.text}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      {(homeCta.title || homeCta.subtitle || (homeCta.buttons && homeCta.buttons.length > 0)) && (
        <section className="cta">
          <div className="container">
            <div className="cta-content">
              <h2>{homeCta.title || 'Prêt à Transformer Votre Pratique ?'}</h2>
              <p>{homeCta.subtitle || 'Rejoignez plus de 500 dentistes qui ont fait confiance à DentoXcellence pour leur développement professionnel'}</p>
              <div className="cta-buttons">
                {(homeCta.buttons || []).map((button, index) => (
                  button.link?.startsWith('http') ? (
                    <a
                      key={index}
                      href={button.link}
                      className={`btn btn-${button.type}`}
                      target={button.target || '_blank'}
                      rel="noopener noreferrer"
                    >
                      <i className={button.icon}></i>
                      {button.text}
                    </a>
                  ) : (
                    <Link key={index} to={button.link || '/contact'} className={`btn btn-${button.type}`}>
                      <i className={button.icon}></i>
                      {button.text}
                    </Link>
                  )
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;

